import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import { useAuthStore } from '../store/authStore';
import { getUnreadCount as fetchGlobalUnread } from '../api/chat';
import { playMessageSound, playUrgentSound } from '../utils/notificationSound';

const ChatContext = createContext(null);

export function useChatContext() {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChatContext must be used within ChatProvider');
  return ctx;
}

export function ChatProvider({ children }) {
  const { token, user } = useAuthStore();
  const socketRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [globalUnreadCount, setGlobalUnreadCount] = useState(0);
  const [channelUnreadCounts, setChannelUnreadCounts] = useState({});
  // Track which channelId is currently focused (open and visible)
  const [activeChannelId, setActiveChannelId] = useState(null);

  // Stable reference for callbacks
  const activeChannelIdRef = useRef(activeChannelId);
  useEffect(() => { activeChannelIdRef.current = activeChannelId; }, [activeChannelId]);

  // Connect the shared socket
  useEffect(() => {
    if (!token || !user) return;

    const apiHost = `${window.location.protocol}//${window.location.hostname}`;
    const socket = io(apiHost, {
      auth: {
        token,
        userId: user.id,
        firstName: user.first_name,
        lastName: user.last_name,
        email: user.email,
      },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 10,
    });

    socketRef.current = socket;

    socket.on('connect', () => setIsConnected(true));
    socket.on('disconnect', () => setIsConnected(false));

    // Global unread update listener
    socket.on('chat:unreadUpdate', ({ channelId, count }) => {
      setChannelUnreadCounts(prev => ({ ...prev, [channelId]: count }));
      // Recalculate global
      setChannelUnreadCounts(prev => {
        const updated = { ...prev, [channelId]: count };
        const total = Object.values(updated).reduce((s, c) => s + c, 0);
        setGlobalUnreadCount(total);
        return updated;
      });
    });

    // Urgent notification
    socket.on('chat:urgent', ({ message }) => {
      if (message.sender_id !== user.id) {
        playUrgentSound();
        if (Notification.permission === 'granted') {
          new Notification('Urgent Message', {
            body: `${message.sender_first} ${message.sender_last}: New urgent message`,
            tag: 'chat-urgent-' + message.id,
          });
        }
      }
    });

    // Play sound for new messages in channels we're not focused on
    socket.on('chat:newMessage', ({ message }) => {
      if (message.sender_id !== user.id && message.channel_id !== activeChannelIdRef.current) {
        playMessageSound();
      }
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
      setIsConnected(false);
    };
  }, [token, user?.id]);

  // Fetch initial unread count
  useEffect(() => {
    if (!token) return;
    fetchGlobalUnread()
      .then(data => setGlobalUnreadCount(data.unread || 0))
      .catch(() => {});
  }, [token]);

  // Refresh unread counts on demand
  const refreshUnread = useCallback(() => {
    fetchGlobalUnread()
      .then(data => setGlobalUnreadCount(data.unread || 0))
      .catch(() => {});
  }, []);

  const getSocket = useCallback(() => socketRef.current, []);

  return (
    <ChatContext.Provider value={{
      socket: socketRef.current,
      getSocket,
      isConnected,
      globalUnreadCount,
      setGlobalUnreadCount,
      channelUnreadCounts,
      setChannelUnreadCounts,
      activeChannelId,
      setActiveChannelId,
      refreshUnread,
    }}>
      {children}
    </ChatContext.Provider>
  );
}
